import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { getSocket } from '@/lib/ws/socket';
import { useConnectionStore } from '@/state/connection.store';
import { ME_QUERY_KEY, type OperatorMe } from '@/lib/me/api';

// Keeps the console's access live — push-driven, never polled (frontend-conventions §8.1).
//
// 1. `WS access.changed` (`operator-bff:WS AccessChanged@v1`, frozen): the BFF emits
//    `{ kind: 'role' | 'assignment' | 'import', accessVersion? }` to the operator's own room
//    on the existing `/cases` socket. No access data rides the event, so the console
//    re-reads `GET /v1/me` — once per event, skipped when the pushed `accessVersion` is
//    one we already hold.
// 2. A 403 anywhere else: the cached access may be stale (an admin removed a role, or the
//    member lost all access → 403 NO_ACCESS_ASSIGNED). Re-read `/v1/me` so the route
//    guards answer with the "Sin acceso asignado" / forbidden views instead of a page that
//    merely failed. Bounded: never while a read is in flight, never twice within
//    `FORBIDDEN_RECHECK_MS` of the last one.
//
// A socket RE-connect already invalidates every query (`lib/ws/socket.ts`), which heals an
// `access.changed` missed while offline.

/** Socket.IO event name of `operator-bff:WS AccessChanged@v1`. */
export const ACCESS_CHANGED_EVENT = 'access.changed';

/** Minimum gap between two 403-triggered re-reads of `/v1/me`. */
export const FORBIDDEN_RECHECK_MS = 10_000;

interface IAccessChangedPush {
  kind?: unknown;
  accessVersion?: unknown;
}

function heldVersion(qc: QueryClient): number | undefined {
  return qc.getQueryData<OperatorMe>(ME_QUERY_KEY)?.access?.accessVersion;
}

/** Subscribe to `access.changed` on the app's socket singleton (read-only use of lib/ws). */
export function useAccessChangedRefresh(): void {
  const qc = useQueryClient();
  // Re-subscribe whenever the connection flips: a new socket after sign-in, or the same
  // socket after a reconnect (Socket.IO keeps listeners on the object, `off` is harmless).
  const connected = useConnectionStore((s) => s.connected);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onChanged = (push?: IAccessChangedPush): void => {
      const pushed = push?.accessVersion;
      const held = heldVersion(qc);
      if (typeof pushed === 'number' && typeof held === 'number' && pushed <= held) return;
      void qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
    };
    socket.on(ACCESS_CHANGED_EVENT, onChanged);
    return () => {
      socket.off(ACCESS_CHANGED_EVENT, onChanged);
    };
  }, [qc, connected]);
}

function isForbidden(error: unknown): boolean {
  if (error instanceof HTTPError) return error.response.status === 403;
  // Forward-compatible with an http client that surfaces the BFF's `code` on the error.
  const code = (error as { code?: unknown } | null)?.code;
  return code === 'NO_ACCESS_ASSIGNED' || code === 'PERMISSION_DENIED';
}

function isMeKey(key: readonly unknown[]): boolean {
  return key.length === ME_QUERY_KEY.length && key.every((k, i) => k === ME_QUERY_KEY[i]);
}

/** Re-read access when any other query or mutation is refused with a 403. */
export function useRefreshOnForbidden(): void {
  const qc = useQueryClient();

  useEffect(() => {
    const recheck = (error: unknown): void => {
      if (!isForbidden(error)) return;
      const state = qc.getQueryState(ME_QUERY_KEY);
      if (state?.fetchStatus === 'fetching') return;
      if (state && Date.now() - state.dataUpdatedAt < FORBIDDEN_RECHECK_MS) return;
      void qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
    };
    const offQueries = qc.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'error') return;
      if (isMeKey(event.query.queryKey)) return;
      recheck(event.action.error);
    });
    const offMutations = qc.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'error') return;
      recheck(event.action.error);
    });
    return () => {
      offQueries();
      offMutations();
    };
  }, [qc]);
}
